import { useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useStudentCourses } from '../../hooks/useLmsData';
import { Spinner } from '../ui/Spinner';
import {
  StudentActionLink,
  StudentBadge,
  StudentCard,
  StudentEmpty,
  StudentGridStats,
  StudentPage,
  StudentProgressBar,
  StudentSection,
  StudentStatCard,
} from './StudentUI';
import {
  formatStudentDate,
  getCourseProgressMessage,
  getStudentCourseAction,
  getStudentStatusMeta,
} from '../../utils/studentExperience';

const StudentCourses = () => {
  const { currentUser } = useAuth();
  const { data, loading, error } = useStudentCourses(currentUser?.uid);
  const courses = useMemo(() => data?.activeCourses || [], [data]);

  const stats = useMemo(
    () => ({
      total: courses.length,
      completed: courses.filter((course) => Number(course.progress || 0) === 100)
        .length,
      inProgress: courses.filter(
        (course) => Number(course.progress || 0) > 0 && Number(course.progress || 0) < 100,
      ).length,
      notStarted: courses.filter((course) => Number(course.progress || 0) === 0).length,
    }),
    [courses],
  );

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <div className="py-8 text-center text-red-600">Failed to load your courses.</div>;
  }

  return (
    <StudentPage
      title="My courses"
      subtitle="Your current learning is grouped into easy cards so you can jump back in quickly."
      action={<StudentActionLink to="/courses/available" variant="secondary">Browse more</StudentActionLink>}
    >
      <StudentGridStats>
        <StudentStatCard label="All active" value={stats.total} detail="Courses ready to open." tone="accent" />
        <StudentStatCard label="In progress" value={stats.inProgress} detail="Keep these moving." tone="success" />
        <StudentStatCard label="Completed" value={stats.completed} detail="Finished courses." tone="success" />
        <StudentStatCard label="Not started" value={stats.notStarted} detail="Good place to begin next." />
      </StudentGridStats>

      <StudentSection
        title="Course cards"
        description="Open any course card to see lessons, payments, and the next step."
      >
        {!courses.length ? (
          <StudentEmpty
            title="No courses yet"
            message="Join your first course to start learning."
            action={<StudentActionLink to="/courses/available">Find a course</StudentActionLink>}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {courses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        )}
      </StudentSection>
    </StudentPage>
  );
};

const CourseCard = ({ course }) => {
  const status = getStudentStatusMeta(course.status);
  const action = getStudentCourseAction(course);

  return (
    <StudentCard className="border-stone-200 bg-white shadow-sm shadow-primary-950/[0.03]">
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold text-stone-900">{course.name}</h3>
            <p className="text-sm leading-6 text-stone-600">
              {course.description || 'Continue your guided learning steps here.'}
            </p>
          </div>
          <StudentBadge tone={status.tone}>{status.label}</StudentBadge>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Info label="Progress" value={`${Number(course.progress || 0)}%`} />
          <Info label="Lessons" value={`${course.completedLessons?.length || 0}/${course.totalLessons || 0}`} />
          <Info label="Batch" value={resolveBatchLabel(course)} />
          <Info label="Started" value={formatStudentDate(course.enrollment?.enrollmentDate, 'Not started')} />
        </div>

        <StudentProgressBar value={Number(course.progress || 0)} />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-stone-500">{getCourseProgressMessage(course.progress)}</p>
          <StudentActionLink to={action.to}>{action.label}</StudentActionLink>
        </div>
      </div>
    </StudentCard>
  );
};

const Info = ({ label, value }) => (
  <div className="rounded-2xl bg-stone-50 px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
      {label}
    </p>
    <p className="mt-1 text-sm font-semibold text-stone-900">{value}</p>
  </div>
);

const resolveBatchLabel = (course) => {
  const batch = (course.batches || []).find(
    (item, index) =>
      String(item.batchId) === String(course.enrollment?.batchId) ||
      String(index) === String(course.enrollment?.batchId),
  );

  return batch?.batchId || course.enrollment?.batchId || 'Assigned';
};

export default StudentCourses;
